import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

type RequireRoleProps = {
  roles: string[];
  children: React.ReactNode;
  fallbackTo?: string;
};

const RequireRole = ({ roles, children, fallbackTo = "/" }: RequireRoleProps) => {
  const [status, setStatus] = useState<"loading" | "allowed" | "denied">("loading");

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        if (!cancelled) setStatus("denied");
        return;
      }

      const [{ data: roleRows }, { data: isSuperAdmin }] = await Promise.all([
        supabase.from("user_roles").select("role").eq("user_id", user.id),
        supabase.rpc("is_super_admin", { _user_id: user.id }),
      ]);

      const resolvedRoles = new Set<string>((roleRows ?? []).map((row) => row.role));
      if (resolvedRoles.has("admin")) {
        resolvedRoles.add("admin");
      }
      if (isSuperAdmin) {
        resolvedRoles.add("super_admin");
        resolvedRoles.add("admin");
      }

      if (!cancelled) {
        const allowed = roles.some((role) => resolvedRoles.has(role));
        setStatus(allowed ? "allowed" : "denied");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [roles]);

  if (status === "loading") {
    return (
      <div className="flex items-center justify-center h-screen">
        Checking access…
      </div>
    );
  }

  if (status === "denied") {
    return <Navigate to={fallbackTo} replace />;
  }

  return <>{children}</>;
};

export default RequireRole;
